"use client";

import { useEffect, useMemo, useState } from "react";

type PremiumRow = {
  symbol: string;
  score: number | null;
  signal: "Long" | "Watch" | "Avoid";
  next_day_up_prob: number | null;
  confidence: number | null;
  expected_move_pct: number | null;
  risk_flag: "Normal" | "Elevated" | "High";
  price: number | null;
  volume: number | null;
  traded_value_mn: number | null;
  volume_rel_1d: number | null;
  value_rel_1d: number | null;
  leverage_oi_score: number | null;
  reasons: string[];
};

type ValidationSource = "live_snapshot" | "walk_forward_backfill";

type ValidationSummary = {
  total_sessions?: number;
  resolved_sessions?: number;
  pending_sessions?: number;
  top1_hit_rate_pct?: number | null;
  top1_avg_return_pct?: number | null;
  basket_avg_return_pct?: number | null;
  basket_win_rate_pct?: number | null;
  latest_session?: string | null;
};

type PremiumPayload = {
  ok: boolean;
  as_of?: string | null;
  top_long?: PremiumRow[];
  watchlist?: PremiumRow[];
  risk_avoid?: PremiumRow[];
  summary?: {
    total_candidates?: number;
    long_count?: number;
    watch_count?: number;
    avoid_count?: number;
    avg_long_prob?: number | null;
    avg_long_expected_move_pct?: number | null;
  };
  backtest?: {
    ok?: boolean;
    sessions?: number;
    cumulative_return_pct?: number;
    win_rate_pct?: number;
    sharpe?: number;
    max_drawdown_pct?: number;
    daily?: Array<{
      date: string;
      top_long_symbol?: string;
      top_long_prob?: number;
      top_long_next_day_return_pct?: number;
      top_long_outcome?: "Win" | "Loss" | "Flat" | string;
      symbols?: string[];
      picks_count?: number;
      avg_return_pct?: number;
      win_rate_pct?: number;
    }>;
  };
  validation_history?: {
    storage?: string;
    total_rows?: number;
    resolved_count?: number;
    recent?: ValidationRow[];
    recent_live?: ValidationRow[];
    recent_walk_forward?: ValidationRow[];
    summary?: {
      live?: ValidationSummary;
      walk_forward?: ValidationSummary;
    };
  };
};

type PremiumResponse = {
  ok: boolean;
  generated_at?: string | null;
  as_of?: string | null;
  diagnostics?: { fallback?: boolean; reason?: string };
  payload?: PremiumPayload;
};

type ValidationRow = {
  source_type?: ValidationSource | string | null;
  session_date: string;
  basket?: unknown[];
  top_long_symbol?: string | null;
  top_long_prob?: number | null;
  top_long_score?: number | null;
  top_long_next_day_return_pct?: number | null;
  top_long_outcome?: "Win" | "Loss" | "Flat" | string | null;
  symbols?: string[];
  picks_count?: number;
  avg_predicted_prob?: number | null;
  avg_predicted_move_pct?: number | null;
  avg_return_pct?: number | null;
  win_rate_pct?: number | null;
  resolved?: boolean;
};

const nf = new Intl.NumberFormat("en-US");

const isFiniteNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

const pctColor = (value: number | null | undefined) => (isFiniteNumber(value) ? (value >= 0 ? "var(--color-success)" : "var(--color-danger)") : "#94a3b8");

const formatFixed = (value: number | null | undefined, digits = 2) => (isFiniteNumber(value) ? value.toFixed(digits) : "-");

// The underlying field is a sigmoid of a confluence score with a bias
// adjustment clamped to +/-6pp. The pipeline's own calibration block reports a
// mean absolute gap of ~18 points against realised outcomes — the 0.65-1.0 bin
// averages 90.6% predicted against 49.1% realised — so this is rendered as a
// 0-100 conviction value, not a percentage chance.
const formatConviction = (value: number | null | undefined, digits = 1) => (isFiniteNumber(value) ? `${(value * 100).toFixed(digits)} / 100` : "-");

const formatPercent = (value: number | null | undefined, digits = 2) => (isFiniteNumber(value) ? `${value.toFixed(digits)}%` : "-");

const formatMultiple = (value: number | null | undefined, digits = 2) => (isFiniteNumber(value) ? `${value.toFixed(digits)}x` : "-");
const monoFont = "'IBM Plex Mono', 'SFMono-Regular', ui-monospace, monospace";

const inferValidationSource = (row: ValidationRow): ValidationSource => {
  const explicit = String(row.source_type || "").trim().toLowerCase();
  if (explicit === "live_snapshot" || explicit === "walk_forward_backfill") {
    return explicit;
  }

  if (Array.isArray(row.basket) && row.basket.length > 0) {
    return "live_snapshot";
  }

  if (row.top_long_score != null || row.avg_predicted_prob != null || row.avg_predicted_move_pct != null) {
    return "live_snapshot";
  }

  return "walk_forward_backfill";
};

const summarizeValidationRows = (rows: ValidationRow[]): ValidationSummary => {
  const resolvedRows = rows.filter((row) => Boolean(row.resolved));
  const top1Returns = rows
    .map((row) => (isFiniteNumber(row.top_long_next_day_return_pct) ? row.top_long_next_day_return_pct : null))
    .filter((value): value is number => value !== null);
  const basketReturns = rows
    .map((row) => (isFiniteNumber(row.avg_return_pct) ? row.avg_return_pct : null))
    .filter((value): value is number => value !== null);
  const basketWinRates = rows
    .map((row) => (isFiniteNumber(row.win_rate_pct) ? row.win_rate_pct : null))
    .filter((value): value is number => value !== null);
  const average = (values: number[]) => (values.length ? values.reduce((total, value) => total + value, 0) / values.length : null);

  return {
    total_sessions: rows.length,
    resolved_sessions: resolvedRows.length,
    pending_sessions: Math.max(rows.length - resolvedRows.length, 0),
    top1_hit_rate_pct: top1Returns.length ? (top1Returns.filter((value) => value > 0).length / top1Returns.length) * 100 : null,
    top1_avg_return_pct: average(top1Returns),
    basket_avg_return_pct: average(basketReturns),
    basket_win_rate_pct: average(basketWinRates),
    latest_session: rows.length ? rows[0]?.session_date ?? rows[rows.length - 1]?.session_date ?? null : null,
  };
};

async function parseResponsePayload(response: Response): Promise<unknown> {
  const contentType = response.headers.get("content-type") || "";
  if (contentType.includes("application/json")) {
    try {
      return await response.json();
    } catch {
      return null;
    }
  }

  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export default function PremiumNextDayPage() {
  const [data, setData] = useState<PremiumResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const showInternalValidation = false;

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch("/api/money-flow/premium/dashboard", { cache: "no-store" });
        const parsed = await parseResponsePayload(res);
        if (!res.ok) {
          const details =
            typeof parsed === "string"
              ? parsed
              : parsed && typeof parsed === "object" && "error" in parsed
                ? String((parsed as { error?: unknown }).error || "Failed to load next-day signals board")
                : "Failed to load next-day signals board";
          throw new Error(details);
        }
        const json = (parsed ?? null) as PremiumResponse | null;
        if (!json || typeof json !== "object") throw new Error("Failed to load next-day signals board");
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load next-day signals board");
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, []);

  const payload = data?.payload;
  const norm = query.trim().toUpperCase();

  const topLong = useMemo(() => {
    const rows = payload?.top_long ?? [];
    if (!norm) return rows;
    return rows.filter((row) => row.symbol.includes(norm));
  }, [payload?.top_long, norm]);

  const watchlist = useMemo(() => {
    const rows = payload?.watchlist ?? [];
    if (!norm) return rows;
    return rows.filter((row) => row.symbol.includes(norm));
  }, [payload?.watchlist, norm]);

  const avoid = useMemo(() => {
    const rows = payload?.risk_avoid ?? [];
    if (!norm) return rows;
    return rows.filter((row) => row.symbol.includes(norm));
  }, [payload?.risk_avoid, norm]);

  const validationRows = useMemo(() => {
    const persisted = payload?.validation_history?.recent ?? [];
    if (persisted.length > 0) return persisted as ValidationRow[];
    const rows = payload?.backtest?.daily ?? [];
    return rows
      .slice(-20)
      .reverse()
      .map(
        (row): ValidationRow => ({
          session_date: row.date,
          top_long_symbol: row.top_long_symbol ?? null,
          top_long_prob: row.top_long_prob ?? null,
          top_long_score: null,
          top_long_next_day_return_pct: row.top_long_next_day_return_pct ?? null,
          top_long_outcome: row.top_long_outcome ?? null,
          symbols: row.symbols,
          picks_count: row.picks_count,
          avg_predicted_prob: null,
          avg_predicted_move_pct: null,
          avg_return_pct: row.avg_return_pct ?? null,
          win_rate_pct: row.win_rate_pct ?? null,
          resolved: true,
          source_type: "walk_forward_backfill",
        }),
      );
  }, [payload?.validation_history?.recent, payload?.backtest?.daily]);

  const liveValidationRows = useMemo(() => {
    const rows = payload?.validation_history?.recent_live ?? [];
    if (rows.length > 0) return rows as ValidationRow[];
    return validationRows.filter((row) => inferValidationSource(row) === "live_snapshot");
  }, [payload?.validation_history?.recent_live, validationRows]);

  const walkForwardValidationRows = useMemo(() => {
    const rows = payload?.validation_history?.recent_walk_forward ?? [];
    if (rows.length > 0) return rows as ValidationRow[];
    return validationRows.filter((row) => inferValidationSource(row) === "walk_forward_backfill");
  }, [payload?.validation_history?.recent_walk_forward, validationRows]);

  const liveValidationSummary = useMemo(
    () => payload?.validation_history?.summary?.live ?? summarizeValidationRows(liveValidationRows),
    [liveValidationRows, payload?.validation_history?.summary?.live],
  );

  const walkForwardValidationSummary = useMemo(
    () => payload?.validation_history?.summary?.walk_forward ?? summarizeValidationRows(walkForwardValidationRows),
    [payload?.validation_history?.summary?.walk_forward, walkForwardValidationRows],
  );

  const generatedLabel = useMemo(() => {
    if (!data?.generated_at) return "-";
    const dt = new Date(data.generated_at);
    return Number.isNaN(dt.getTime()) ? data.generated_at : dt.toLocaleString();
  }, [data?.generated_at]);

  const renderTable = (title: string, rows: PremiumRow[], empty: string) => (
    <section style={{ border: "1px solid #1f2937", borderRadius: 12, overflow: "hidden", marginBottom: 14 }}>
      <div style={{ padding: "10px 12px", borderBottom: "1px solid #1f2937", fontWeight: 700 }}>{title}</div>
      {rows.length === 0 ? (
        <p style={{ margin: 0, padding: 12, color: "#94a3b8" }}>{empty}</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #1f2937", color: "#94a3b8", textAlign: "left" }}>
                <th style={{ padding: "9px 10px" }}>Symbol</th>
                <th style={{ padding: "9px 10px" }}>Score</th>
                <th style={{ padding: "9px 10px" }} title="Conviction 0-100 — not a probability">Conviction</th>
                <th style={{ padding: "9px 10px" }}>Exp Move</th>
                <th style={{ padding: "9px 10px" }}>Liquidity</th>
                <th style={{ padding: "9px 10px" }}>Lev+OI</th>
                <th style={{ padding: "9px 10px" }}>Risk</th>
                <th style={{ padding: "9px 10px" }}>Reasons</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${title}-${row.symbol}`} style={{ borderBottom: "1px solid #111827" }}>
                  <td style={{ padding: "9px 10px", fontWeight: 700 }}>
                    {row.symbol}
                    <div style={{ fontSize: 11, color: "#64748b", fontWeight: 400 }}>
                      PKR {formatFixed(row.price)} · {isFiniteNumber(row.volume) ? nf.format(row.volume) : "-"} sh
                    </div>
                  </td>
                  <td style={{ padding: "9px 10px", color: "#e2e8f0" }}>{formatFixed(row.score, 1)}</td>
                  <td style={{ padding: "9px 10px", color: isFiniteNumber(row.next_day_up_prob) && row.next_day_up_prob >= 0.58 ? "var(--color-success)" : "#f59e0b" }}>
                    {formatConviction(row.next_day_up_prob)}
                  </td>
                  <td style={{ padding: "9px 10px", color: pctColor(row.expected_move_pct) }}>{formatPercent(row.expected_move_pct)}</td>
                  <td style={{ padding: "9px 10px" }}>
                    {isFiniteNumber(row.traded_value_mn) ? `${row.traded_value_mn.toFixed(1)}M` : "-"}
                    <div style={{ fontSize: 11, color: "#64748b" }}>
                      vRel {formatMultiple(row.volume_rel_1d)} · valRel {formatMultiple(row.value_rel_1d)}
                    </div>
                  </td>
                  <td style={{ padding: "9px 10px", color: isFiniteNumber(row.leverage_oi_score) && row.leverage_oi_score >= 0 ? "var(--color-success)" : "var(--color-danger)" }}>
                    {formatFixed(row.leverage_oi_score)}
                  </td>
                  <td style={{ padding: "9px 10px", color: row.risk_flag === "High" ? "var(--color-danger)" : row.risk_flag === "Elevated" ? "#f59e0b" : "var(--color-success)" }}>
                    {row.risk_flag}
                  </td>
                  <td style={{ padding: "9px 10px", color: "#cbd5e1" }}>{row.reasons.join(" · ") || "balanced"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );

  const renderValidationTable = (
    title: string,
    subtitle: string,
    rows: ValidationRow[],
    empty: string,
    tone: string,
    modeLabel: string,
  ) => (
    <section
      style={{
        border: `1px solid ${tone}26`,
        borderRadius: 16,
        overflow: "hidden",
        marginBottom: 16,
        background:
          `radial-gradient(circle at top left, ${tone}18, transparent 26%), repeating-linear-gradient(180deg, rgba(148,163,184,0.03) 0, rgba(148,163,184,0.03) 1px, transparent 1px, transparent 24px), linear-gradient(180deg, rgba(5, 10, 18, 0.96), rgba(10, 16, 26, 0.92))`,
        boxShadow: "inset 0 1px 0 rgba(148, 163, 184, 0.08), 0 18px 32px rgba(2, 6, 23, 0.24)",
      }}
    >
      <div
        style={{
          padding: "12px 14px",
          borderBottom: "1px solid rgba(42, 50, 65, 0.92)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 12,
          flexWrap: "wrap",
          background: "linear-gradient(180deg, rgba(15,23,42,0.74), rgba(15,23,42,0.38))",
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              color: tone,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              fontFamily: monoFont,
              fontWeight: 700,
            }}
          >
            {modeLabel}
          </div>
          <div style={{ marginTop: 6, fontWeight: 700, color: "#f8fafc" }}>{title}</div>
          <div style={{ marginTop: 4, color: "#94a3b8", fontSize: 12, maxWidth: 760 }}>{subtitle}</div>
        </div>
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 10,
            padding: "0.55rem 0.75rem",
            borderRadius: 999,
            border: `1px solid ${tone}33`,
            background: "rgba(15, 23, 42, 0.78)",
            color: "#cbd5e1",
            fontSize: 12,
            fontFamily: monoFont,
          }}
        >
          <span>{rows.length} rows</span>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: tone, boxShadow: `0 0 12px ${tone}` }} />
        </div>
      </div>
      {rows.length === 0 ? (
        <p
          style={{
            margin: 0,
            padding: 18,
            color: "#94a3b8",
            fontFamily: monoFont,
            fontSize: 12,
            letterSpacing: "0.04em",
          }}
        >
          {empty}
        </p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
            <thead>
              <tr style={{ borderBottom: "1px solid #1f2937", color: "#94a3b8", textAlign: "left", background: "rgba(15, 23, 42, 0.82)" }}>
                {["Session", "Top Long", "Top Score", "Top Conviction", "Picks", "Top Next Day", "Outcome", "Basket Avg", "Basket Win Rate"].map((label) => (
                  <th
                    key={label}
                    style={{
                      padding: "10px 12px",
                      fontSize: 11,
                      letterSpacing: "0.12em",
                      textTransform: "uppercase",
                      fontFamily: monoFont,
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr
                  key={`val-${title}-${row.session_date}-${row.top_long_symbol || "na"}`}
                  style={{
                    borderBottom: "1px solid rgba(17, 24, 39, 0.95)",
                    background: index % 2 === 0 ? "rgba(255,255,255,0.015)" : "transparent",
                  }}
                >
                  <td style={{ padding: "10px 12px" }}>
                    <div style={{ color: "#f8fafc", fontFamily: monoFont, fontSize: 12 }}>{row.session_date || "-"}</div>
                    <div style={{ marginTop: 4 }}>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          padding: "0.18rem 0.5rem",
                          borderRadius: 999,
                          border: `1px solid ${(row.resolved === false ? "#f59e0b" : tone)}33`,
                          background: row.resolved === false ? "rgba(245, 158, 11, 0.12)" : `${tone}12`,
                          color: row.resolved === false ? "#fbbf24" : tone,
                          fontSize: 10,
                          letterSpacing: "0.1em",
                          textTransform: "uppercase",
                          fontFamily: monoFont,
                          fontWeight: 700,
                        }}
                      >
                        {row.resolved === false ? "Pending" : "Resolved"}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 12px", fontWeight: 700, color: "#f8fafc" }}>{row.top_long_symbol || "-"}</td>
                  <td style={{ padding: "10px 12px" }} className="tabular-nums">
                    {formatFixed(row.top_long_score, 1)}
                  </td>
                  <td style={{ padding: "10px 12px" }} className="tabular-nums">
                    {formatConviction(row.top_long_prob)}
                  </td>
                  <td style={{ padding: "10px 12px" }} className="tabular-nums">
                    {isFiniteNumber(row.picks_count) ? nf.format(row.picks_count) : "-"}
                  </td>
                  <td
                    className="tabular-nums"
                    style={{
                      padding: "10px 12px",
                      color: isFiniteNumber(row.top_long_next_day_return_pct) ? pctColor(row.top_long_next_day_return_pct) : "#e2e8f0",
                      fontWeight: 700,
                    }}
                  >
                    {formatPercent(row.top_long_next_day_return_pct)}
                  </td>
                  <td
                    style={{
                      padding: "10px 12px",
                      color:
                        row.top_long_outcome === "Win"
                          ? "var(--color-success)"
                          : row.top_long_outcome === "Loss"
                            ? "var(--color-danger)"
                          : "#94a3b8",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        padding: "0.2rem 0.55rem",
                        borderRadius: 999,
                        border:
                          row.top_long_outcome === "Win"
                            ? "1px solid rgba(34, 197, 94, 0.3)"
                            : row.top_long_outcome === "Loss"
                              ? "1px solid rgba(248, 113, 113, 0.32)"
                              : "1px solid rgba(148, 163, 184, 0.22)",
                        background:
                          row.top_long_outcome === "Win"
                            ? "rgba(34, 197, 94, 0.12)"
                            : row.top_long_outcome === "Loss"
                              ? "rgba(248, 113, 113, 0.12)"
                              : "rgba(148, 163, 184, 0.08)",
                        fontSize: 11,
                        fontFamily: monoFont,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        fontWeight: 700,
                      }}
                    >
                      {row.top_long_outcome || (row.resolved === false ? "Pending" : "-")}
                    </span>
                  </td>
                  <td style={{ padding: "10px 12px", color: pctColor(row.avg_return_pct), fontWeight: 700 }} className="tabular-nums">
                    {formatPercent(row.avg_return_pct)}
                  </td>
                  <td style={{ padding: "10px 12px" }} className="tabular-nums">
                    {row.resolved === false ? "Pending" : formatPercent(row.win_rate_pct, 0)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );

  return (
    <main style={{ padding: "28px", color: "#e2e8f0" }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ margin: 0, color: "#f8fafc", fontSize: 28 }}>Next-Day Signals Board</h1>
        <p style={{ marginTop: 8, color: "#94a3b8" }}>
          Cross-source next-session board combining leverage + OI, UIN settlement quality, off-market blocks, flow context, and liquidity confluence.
        </p>
      </div>

      <section
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
          gap: 10,
          marginBottom: 14,
        }}
      >
        <div style={{ border: "1px solid #1f2937", borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>As Of</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{payload?.as_of || data?.as_of || "-"}</div>
        </div>
        <div style={{ border: "1px solid #1f2937", borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>Long Candidates</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{nf.format(payload?.summary?.long_count ?? 0)}</div>
        </div>
        <div style={{ border: "1px solid #1f2937", borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 12, color: "#94a3b8" }} title="Average conviction of the long picks, 0-100. Not a probability — see the calibration block in premium_next_day_dashboard.json">Avg Long Conviction</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--color-success)" }}>
            {formatConviction(payload?.summary?.avg_long_prob)}
          </div>
        </div>
        <div style={{ border: "1px solid #1f2937", borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>Backtest Sharpe</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{formatFixed(payload?.backtest?.sharpe)}</div>
        </div>
        <div style={{ border: "1px solid #1f2937", borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>Backtest Win Rate</div>
          <div style={{ fontSize: 18, fontWeight: 700 }}>{formatPercent(payload?.backtest?.win_rate_pct, 1)}</div>
        </div>
        <div style={{ border: "1px solid #1f2937", borderRadius: 12, padding: 12 }}>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>Updated</div>
          <div style={{ fontSize: 14, fontWeight: 700 }}>{generatedLabel}</div>
        </div>
      </section>

      <section
        style={{
          border: "1px solid #1f2937",
          borderRadius: 12,
          padding: 12,
          marginBottom: 12,
          display: "grid",
          gap: 10,
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        }}
      >
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Filter symbol (e.g. UBL)"
          style={{ background: "#0f172a", border: "1px solid #334155", color: "#e2e8f0", borderRadius: 8, padding: "10px" }}
        />
        <div style={{ color: "#94a3b8", fontSize: 13, alignSelf: "center" }}>
          Coverage: {nf.format(payload?.summary?.total_candidates ?? 0)} candidates | Watch: {nf.format(payload?.summary?.watch_count ?? 0)} | Avoid:{" "}
          {nf.format(payload?.summary?.avoid_count ?? 0)}
        </div>
      </section>

      {loading ? <p>Loading next-day signals...</p> : null}
      {error ? <p style={{ color: "var(--color-danger)" }}>{error}</p> : null}
      {!loading && !error && data?.diagnostics?.fallback ? (
        <p style={{ color: "#f59e0b", marginBottom: 12 }}>{data.diagnostics.reason || "Next-day signals output missing."}</p>
      ) : null}

      {!loading && !error && payload?.ok ? (
        <>
          {renderTable("Top Long Candidates", topLong, "No long candidates for current filters.")}
          {renderTable("Watchlist", watchlist, "No watchlist symbols for current filters.")}
          {renderTable("Risk / Avoid", avoid, "No avoid symbols for current filters.")}
          {showInternalValidation && (
            <>
          <section
            style={{
              border: "1px solid rgba(59, 130, 246, 0.18)",
              borderRadius: 18,
              padding: 16,
              marginBottom: 16,
              overflow: "hidden",
              position: "relative",
              background:
                "radial-gradient(circle at top left, rgba(59,130,246,0.16), transparent 28%), repeating-linear-gradient(180deg, rgba(148,163,184,0.03) 0, rgba(148,163,184,0.03) 1px, transparent 1px, transparent 26px), linear-gradient(180deg, rgba(5,10,18,0.97), rgba(10,16,26,0.92))",
              boxShadow: "inset 0 1px 0 rgba(148, 163, 184, 0.08), 0 20px 36px rgba(2, 6, 23, 0.22)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 12,
                flexWrap: "wrap",
                marginBottom: 10,
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: "#7dd3fc", letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: monoFont, fontWeight: 700 }}>
                  Validation Console
                </div>
                <div style={{ marginTop: 6, fontWeight: 700, fontSize: 18, color: "#f8fafc" }}>Ranking quality and signal edge need separate scoreboards.</div>
              </div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "0.55rem 0.8rem",
                  borderRadius: 999,
                  border: "1px solid rgba(59, 130, 246, 0.24)",
                  background: "rgba(15, 23, 42, 0.72)",
                  color: "#cbd5e1",
                  fontSize: 12,
                  fontFamily: monoFont,
                }}
              >
                <span>{payload?.validation_history?.storage || "derived"}</span>
                <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#38bdf8", boxShadow: "0 0 12px rgba(56, 189, 248, 0.9)" }} />
              </div>
            </div>
            <p style={{ margin: "0", color: "#94a3b8", lineHeight: 1.68, maxWidth: 980 }}>
              Live Production stores the actual day-end picks and resolves them on the next session. Walk-Forward Baseline is a seeded benchmark used to judge whether the ranking logic has edge before the live sample becomes statistically meaningful.
            </p>
            <div
              style={{
                marginTop: 12,
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 10,
              }}
            >
              {[
                { label: "History Rows", value: nf.format(payload?.validation_history?.total_rows ?? validationRows.length), tone: "#cbd5e1" },
                { label: "Resolved", value: nf.format(payload?.validation_history?.resolved_count ?? 0), tone: "var(--color-success)" },
                {
                  label: "Pending",
                  value: nf.format(Math.max((payload?.validation_history?.total_rows ?? validationRows.length) - (payload?.validation_history?.resolved_count ?? 0), 0)),
                  tone: "#f59e0b",
                },
              ].map((item) => (
                <div
                  key={item.label}
                  style={{
                    padding: "0.8rem 0.9rem",
                    borderRadius: 12,
                    border: "1px solid rgba(42, 50, 65, 0.96)",
                    background: "rgba(15, 23, 42, 0.48)",
                  }}
                >
                  <div style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: monoFont }}>
                    {item.label}
                  </div>
                  <div style={{ marginTop: 6, fontSize: 18, fontWeight: 700, color: item.tone }} className="tabular-nums">
                    {item.value}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 12,
              marginBottom: 14,
            }}
          >
            {[
              {
                title: "Live Production Validation",
                summary: liveValidationSummary,
                tone: "var(--color-success)",
                copy: "Use this to judge whether the top-ranked live pick and the live basket are actually holding up after the close.",
              },
              {
                title: "Walk-Forward Baseline",
                summary: walkForwardValidationSummary,
                tone: "#38bdf8",
                copy: "Use this as the historical expectation layer so you can compare live performance versus the seeded benchmark.",
              },
            ].map((card) => (
              <div
                key={card.title}
                style={{
                  border: `1px solid ${card.tone}2d`,
                  borderRadius: 16,
                  padding: 16,
                  background:
                    `radial-gradient(circle at top left, ${card.tone}16, transparent 30%), linear-gradient(180deg, rgba(8, 13, 21, 0.96), rgba(11, 18, 28, 0.92))`,
                  boxShadow: "inset 0 1px 0 rgba(148, 163, 184, 0.08), 0 18px 28px rgba(2, 6, 23, 0.2)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ fontSize: 11, color: card.tone, letterSpacing: "0.14em", textTransform: "uppercase", fontFamily: monoFont, fontWeight: 700 }}>
                      {card.title === "Live Production Validation" ? "Live Edge Monitor" : "Historical Benchmark"}
                    </div>
                    <div style={{ marginTop: 6, fontWeight: 700, color: "#f8fafc" }}>{card.title}</div>
                  </div>
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "0.45rem 0.7rem",
                      borderRadius: 999,
                      border: `1px solid ${card.tone}33`,
                      background: "rgba(15, 23, 42, 0.74)",
                      color: "#cbd5e1",
                      fontSize: 12,
                      fontFamily: monoFont,
                    }}
                  >
                    <span className="tabular-nums">{nf.format(card.summary.total_sessions ?? 0)} sessions</span>
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: card.tone, boxShadow: `0 0 12px ${card.tone}` }} />
                  </div>
                </div>
                <p style={{ margin: "0 0 12px", color: "#94a3b8", fontSize: 13, lineHeight: 1.62 }}>{card.copy}</p>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(120px, 1fr))", gap: 10 }}>
                  <div style={{ padding: "0.75rem 0.8rem", borderRadius: 12, border: "1px solid rgba(42, 50, 65, 0.96)", background: "rgba(15, 23, 42, 0.46)" }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: monoFont }}>Sessions</div>
                    <div style={{ marginTop: 6, fontSize: 17, fontWeight: 700 }} className="tabular-nums">{nf.format(card.summary.total_sessions ?? 0)}</div>
                  </div>
                  <div style={{ padding: "0.75rem 0.8rem", borderRadius: 12, border: "1px solid rgba(42, 50, 65, 0.96)", background: "rgba(15, 23, 42, 0.46)" }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: monoFont }}>Resolved / Pending</div>
                    <div style={{ marginTop: 6, fontSize: 17, fontWeight: 700 }} className="tabular-nums">
                      {nf.format(card.summary.resolved_sessions ?? 0)} / {nf.format(card.summary.pending_sessions ?? 0)}
                    </div>
                  </div>
                  <div style={{ padding: "0.75rem 0.8rem", borderRadius: 12, border: "1px solid rgba(42, 50, 65, 0.96)", background: "rgba(15, 23, 42, 0.46)" }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: monoFont }}>Top 1 Hit Rate</div>
                    <div style={{ marginTop: 6, fontSize: 17, fontWeight: 700, color: card.tone }} className="tabular-nums">{formatPercent(card.summary.top1_hit_rate_pct, 1)}</div>
                  </div>
                  <div style={{ padding: "0.75rem 0.8rem", borderRadius: 12, border: "1px solid rgba(42, 50, 65, 0.96)", background: "rgba(15, 23, 42, 0.46)" }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: monoFont }}>Top 1 Avg Return</div>
                    <div style={{ marginTop: 6, fontSize: 17, fontWeight: 700, color: pctColor(card.summary.top1_avg_return_pct) }} className="tabular-nums">
                      {formatPercent(card.summary.top1_avg_return_pct)}
                    </div>
                  </div>
                  <div style={{ padding: "0.75rem 0.8rem", borderRadius: 12, border: "1px solid rgba(42, 50, 65, 0.96)", background: "rgba(15, 23, 42, 0.46)" }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: monoFont }}>Top 5 Basket Avg</div>
                    <div style={{ marginTop: 6, fontSize: 17, fontWeight: 700, color: pctColor(card.summary.basket_avg_return_pct) }} className="tabular-nums">
                      {formatPercent(card.summary.basket_avg_return_pct)}
                    </div>
                  </div>
                  <div style={{ padding: "0.75rem 0.8rem", borderRadius: 12, border: "1px solid rgba(42, 50, 65, 0.96)", background: "rgba(15, 23, 42, 0.46)" }}>
                    <div style={{ fontSize: 11, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", fontFamily: monoFont }}>Top 5 Basket Win Rate</div>
                    <div style={{ marginTop: 6, fontSize: 17, fontWeight: 700 }} className="tabular-nums">{formatPercent(card.summary.basket_win_rate_pct, 1)}</div>
                  </div>
                </div>
              </div>
            ))}
          </section>

          {renderValidationTable(
            "Live Production Log",
            "Actual daily snapshots captured from the current engine and resolved on the next session.",
            liveValidationRows,
            "No live production snapshots have been captured yet.",
            "var(--color-success)",
            "Live Validation Feed",
          )}
          {renderValidationTable(
            "Walk-Forward Baseline",
            "Seeded historical benchmark used to estimate expected edge before the live sample matures.",
            walkForwardValidationRows,
            "No walk-forward validation rows are available yet.",
            "#38bdf8",
            "Baseline Validation Feed",
          )}
            </>
          )}
        </>
      ) : null}
    </main>
  );
}
